import { Box, Typography, useTheme } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { FlatRowTriggerButton } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerButton";
import { FlatRowTriggerValue } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerValue";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FlatRowSelectOption<TValue extends string | number = string> = {
	value: TValue;
	label: string;
};

export type FlatRowSelectProps<TValue extends string | number = string> = {
	label: string;
	value: TValue | "";
	options: FlatRowSelectOption<TValue>[];
	onChange: (value: TValue) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	isLast?: boolean;
	error?: boolean;
	helperText?: string;
	placeholder?: string;
};

export const FlatRowSelect = <TValue extends string | number = string>({
	label,
	value,
	options,
	onChange,
	onBlur,
	drawerTitle,
	isLast,
	error,
	helperText,
	placeholder,
}: FlatRowSelectProps<TValue>) => {
	const theme = useTheme();
	const hasGoalsRight = useGoalsRightOptional();
	const [open, setOpen] = useState(false);

	const selectedLabel =
		options.find((option) => String(option.value) === String(value))?.label ??
		"";

	const handleSelect = (optionValue: TValue) => {
		onChange(optionValue);
		setOpen(false);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	return (
		<Box>
			<FlatRowTriggerButton
				onClick={() => setOpen(true)}
				sx={{
					borderBottom: isLast
						? "none"
						: `1px solid ${theme.palette.surface.border}`,
				}}
			>
				<Typography
					variant="body2"
					color={error ? theme.palette.error.main : theme.palette.grey[500]}
					flexShrink={0}
					mr={2}
					lineHeight={`${VALUE_LINE_HEIGHT}px`}
				>
					{label}
				</Typography>
				<FlatRowTriggerValue
					value={selectedLabel}
					placeholder={placeholder}
					icon={hasGoalsRight ? ChevronDown : undefined}
				/>
			</FlatRowTriggerButton>

			{error && helperText && (
				<Typography
					component="p"
					variant="caption"
					color={theme.palette.error.main}
					px={1.6}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
			>
				<FlatRowSelectOptionList
					options={options}
					value={value}
					onSelect={handleSelect}
				/>
			</MyPlannDrawer>
		</Box>
	);
};
